import Checkbox from 'src/components/Checkbox';
import Card from 'src/components/Card';
import FontAwesomeIcon from 'src/components/Icon';
import CalcomConnectionBadge from 'src/components/CalcomConnectionBadge/CalcomConnectionBadge';
import CalcomBookingLinkPicker from 'src/components/CalcomBookingLinkPicker/CalcomBookingLinkPicker';
import ConnectedCalendars from 'src/pages/Calendar/CalcomIntegration/components/ConnectedCalendars/ConnectedCalendars';
import { ICalcomConnectedCalendar } from 'src/pages/Calendar/CalcomIntegration/types';
import Callout from 'src/components/InformationCallout/Callout';
import React from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import {
  ActivationActions
} from 'src/components/BulkOperations/Walkthroughs/BulkActivationWalkthroughsDialogue/BulkActivateWalkthroughsDialogue';
import './WalkthroughActivationSection.scss';
import { variables } from 'src/Tenants';

interface WalkthroughActivationSectionProps {
  activateWalkthroughs: boolean;
  sendSchedulingEmail: boolean;
  actions: ActivationActions;
  disabled?: boolean;
  className?: string;
  /* Supplying a propertyId turns on the Cal.com scheduling controls; both the bulk-add and
   * activate flows pass it. Omit it to render the plain section without scheduling */
  propertyId?: string;
  isCalcomConnected?: boolean;
  connectedCalendars?: ICalcomConnectedCalendar[];
  selectedBookingLinkId?: string | null;
  onSelectBookingLink?: (bookingLinkId: string | null) => void;
}

const WalkthroughActivationSection = ({
  activateWalkthroughs,
  sendSchedulingEmail,
  actions,
  disabled = false,
  className = '',
  propertyId,
  isCalcomConnected = false,
  connectedCalendars = [],
  selectedBookingLinkId = null,
  onSelectBookingLink,
}: WalkthroughActivationSectionProps) => {
  const { setActivate, setSendEmail }: ActivationActions = actions;

  const isCalcomScheduling: boolean = propertyId !== undefined;

  const isEmailDisabled: boolean = !activateWalkthroughs || disabled;

  const handleActivateChange = () => {
    const newValue: boolean = !activateWalkthroughs;
    setActivate(newValue);

    // If activating is turned off, also turn off email sending.
    if (!newValue && sendSchedulingEmail) {
      setSendEmail(false);
    }
  };

  const handleEmailChange = () => {
    if (activateWalkthroughs) {
      setSendEmail(!sendSchedulingEmail);
    }
  };

  const suiteName: string = variables.suiteName;

  return (
    <div className={`activationOptions ${className}`}>
      <div className="activationGroup">
        <Checkbox
          label={`Activate walkthroughs for selected ${suiteName}`}
          value={activateWalkthroughs}
          onChange={handleActivateChange}
          disabled={disabled}
          data-cy="activate-walkthroughs"
        />
        <p className={`helperText ${disabled ? 'disabled' : ''}`}>
          Once activated, walkthroughs will be available in the mobile app, and
          for homeowners to schedule (if selected below)
        </p>
      </div>

      <div className="activationGroup">
        <Checkbox
          label="Send scheduling email to homeowners"
          value={sendSchedulingEmail}
          onChange={handleEmailChange}
          disabled={isEmailDisabled}
          data-cy="send-scheduling-email"
        />
        <p className={`helperText ${isEmailDisabled ? 'disabled' : ''}`}>
          Homeowners will receive an email invitation to schedule their
          walkthrough
        </p>

        {isCalcomScheduling &&
          sendSchedulingEmail &&
          (isCalcomConnected ? (
            <div className="calcomScheduling">
              <CalcomConnectionBadge isConnected />
              <CalcomBookingLinkPicker
                propertyId={propertyId as string}
                selectedValue={selectedBookingLinkId}
                onChange={(bookingLinkId: string | null) =>
                  onSelectBookingLink?.(bookingLinkId)
                }
                required={sendSchedulingEmail}
              />
              <Callout
                variant="info"
                title="How homeowner scheduling works with Cal.com"
                subtitle={
                  <>
                    When you send, Juniper emails each selected homeowner an
                    invitation with this booking link. They click it and pick a
                    walkthrough time themselves, from your live Cal.com
                    availability — the connected calendars below. Each booking
                    takes that slot on your calendars, so two homeowners
                    can&apos;t land on the same time: first-come, first-served,
                    no double-booking.
                  </>
                }
              >
                <ConnectedCalendars
                  calendars={connectedCalendars}
                  compact
                  title="Availability is checked against the calendars connected to your Cal.com account:"
                />
              </Callout>
            </div>
          ) : (
            <div className="calcomScheduling">
              <CalcomConnectionBadge isConnected={false} />
              <Callout
                variant="info"
                title="Scheduling emails will use Juniper's built-in scheduling"
                subtitle="Cal.com isn't connected, so homeowners will book through Juniper's own scheduling system rather than live Cal.com availability. Any walkthroughs scheduled this way stay locked to Juniper's system — and likewise, ones booked through Cal.com stay there — so switching later won't migrate existing bookings. Connect Cal.com to switch to real-time calendar booking."
              >
                <Link
                  to={PATHS.CALENDAR_CALCOM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="calcomScheduling-cta"
                >
                  Connect Cal.com
                </Link>
              </Callout>
            </div>
          ))}

        {!activateWalkthroughs && !disabled && (
          <div className="infoCard">
            <Card
              body={
                <div className="cardContent">
                  <FontAwesomeIcon glyph="info-circle" weight="light" />
                  <span>
                    Walkthroughs must be activated to send scheduling emails
                  </span>
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WalkthroughActivationSection;
